import type { AuthorizeNetFrontendService } from '@supply-mover-2-0/core';
import {
	PaymentGatewayError,
	PaymentMethodTypeEnum,
	detectCreditCardSubtype,
	supportsExpirationDateUpdate,
	type PaymentMethod,
	type PaymentMethodFormData,
	type SupportedPaymentMethodType,
} from '@supply-mover-2-0/core';
import { US_STATES } from '@supply-mover-2-0/shared';
import React, { useEffect, useRef, useState } from 'react';
import { useThemeColors } from '../providers/ThemeProvider';
import { useTranslation } from '../providers/TranslationProvider';
import { cn } from '../utils/cn';
import { Text } from './base/Text';
import { Button } from './forms/Button';
import { Input } from './forms/Input';
import { Select } from './forms/Select';
import { CardBrandIcon } from './payments/CardBrandIcon';

export type {
	PaymentMethod,
	PaymentMethodFormData,
	SupportedPaymentMethodType,
};

/**
 * Supported-method entry the form accepts. Derives from the canonical
 * SupportedPaymentMethodType but allows a null processingFee — the portal
 * checkout DTO nulls fees for hide-pricing buyers (PD-14239), and this form
 * only ever reads the method type, never the fee.
 */
export type PaymentMethodFormSupportedMethod = Omit<
	SupportedPaymentMethodType,
	'processingFee'
> & {
	processingFee: SupportedPaymentMethodType['processingFee'] | null;
};

interface PaymentMethodFormProps {
	// Mode configuration
	mode: 'create' | 'edit' | 'inline';

	// Data
	clientId?: number;
	supportedPaymentMethods?: Array<PaymentMethodFormSupportedMethod>;
	editPaymentMethod?: PaymentMethod;

	// Event handlers.
	// onSubmit resolves `true` on success and `false` when the submission
	// FAILED but the failure was already surfaced to the user by the handler
	// (toast / inline error) — the form then keeps its data so the user can
	// correct and retry. Throwing means failure that the form itself should
	// display (also keeps the data).
	onSubmit: (data: PaymentMethodFormData) => Promise<boolean>;
	onCancel?: () => void;

	// Display configuration
	showTypeSelector?: boolean;
	allowedPaymentTypes?: Array<PaymentMethodTypeEnum>;
	preSelectedPaymentType?: PaymentMethodTypeEnum;
	isEditMode?: boolean; // Explicit edit mode flag
	className?: string;
	// Class override for the submit button so branded hosts (the tenant
	// storefront) can restyle it to their accent color — the default
	// `variant="primary"` is the CRM blue. Must include dark:-scoped bg
	// classes to fully displace the variant's dark-mode styles
	// (tailwind-merge keeps a dark:bg-* that has no incoming counterpart).
	submitButtonClassName?: string;
	// Same override for the Cancel button (PD-15014) — its default
	// `variant="outline"` styles with the CRM theme's neutral border/text
	// tokens, which read as washed-out gray against a brand-tinted panel.
	cancelButtonClassName?: string;

	// Payment processor configuration
	processorType?: string;
	/**
	 * PD-15878 — the card networks the tenant's processor accepts, shown as
	 * marks beside the Card Number label so the customer can see at a glance
	 * whether their card will be taken.
	 *
	 * `null`/omitted means the gateway could not report its configuration —
	 * which is NOT the same as accepting nothing, so the strip is simply not
	 * rendered rather than shown empty.
	 */
	acceptedCardBrands?: Array<string> | null;
	// Callback when secure card field containers are mounted (for CyberSource / NMI hosted fields)
	onSecureFieldsReady?: (
		cardNumberContainerId: string,
		securityCodeContainerId: string,
	) => void;
	// Callback when secure expiration field container is mounted (NMI needs 3 card fields)
	onSecureExpirationFieldReady?: (expirationContainerId: string) => void;
	// Callback when secure ACH field containers are mounted (for CyberSource / NMI hosted fields).
	// nameContainerId is only provided for NMI — Collect.JS requires the
	// name-on-account (checkname) to be a hosted field for ACH tokenization.
	onSecureACHFieldsReady?: (
		routingNumberContainerId: string,
		accountNumberContainerId: string,
		nameContainerId?: string,
	) => void;
	// Callback when payment type changes (for cleanup during type switch)
	onPaymentTypeChange?: (paymentType: PaymentMethodTypeEnum) => void;
	// Default email for billing (typically from client record)
	defaultEmail?: string;
}

/**
 * Formats a card number with spaces every 4 digits
 * e.g., "4111111111111111" -> "4111 1111 1111 1111"
 */
const formatCardNumber = (value: string): string => {
	// Remove all non-digit characters
	const digits = value.replace(/\D/g, '');

	// Limit to 16 digits
	const limited = digits.slice(0, 16);

	// Add space every 4 digits
	const formatted = limited.replace(/(\d{4})(?=\d)/g, '$1 ');

	return formatted;
};

/**
 * Formats an expiration date with slash between month and year
 * e.g., "1225" -> "12/25", "122025" -> "12/2025"
 */
const formatExpirationDate = (value: string): string => {
	// Remove all non-digit characters
	const digits = value.replace(/\D/g, '');

	// Limit to 6 digits (MM/YYYY)
	const limited = digits.slice(0, 6);

	if (limited.length === 0) {
		return '';
	}

	if (limited.length <= 2) {
		return limited;
	}

	// Add slash after month
	return `${limited.slice(0, 2)}/${limited.slice(2)}`;
};

/**
 * Map whatever a stored billing state holds — a clean code, a lowercase or
 * padded code, or a full state name — onto a US_STATES code, so a payment
 * method saved before the state field became a dropdown (PD-15304) still
 * selects correctly in edit mode. Unmappable values return '' (placeholder).
 */
const toStateCode = (raw: string | null | undefined): string => {
	const trimmed = raw?.trim() ?? '';
	if (trimmed === '') {
		return '';
	}
	const upper = trimmed.toUpperCase();
	if (US_STATES.some((state) => state.value === upper)) {
		return upper;
	}
	return (
		US_STATES.find((state) => state.label.toUpperCase() === upper)?.value ?? ''
	);
};

const getInitialFormData = (
	preSelectedType?: PaymentMethodTypeEnum,
	defaultEmail?: string,
): PaymentMethodFormData => ({
	paymentType: preSelectedType || PaymentMethodTypeEnum.CREDIT_CARD,
	// Credit card fields
	cardNumber: '',
	expirationDate: '',
	nameOnCard: '',
	securityCode: '',
	// eCheck/ACH fields
	routingNumber: '',
	accountNumber: '',
	nameOnAccount: '',
	accountType: 'checking',
	// Address fields
	addressLine1: '',
	addressLine2: '',
	zipCode: '',
	state: '',
	city: '',
	// Email
	email: defaultEmail ?? '',
});

// Container IDs for CyberSource Flex Microform
const CYBERSOURCE_CARD_NUMBER_CONTAINER = 'cybersource-card-number';
const CYBERSOURCE_SECURITY_CODE_CONTAINER = 'cybersource-security-code';
const CYBERSOURCE_ROUTING_NUMBER_CONTAINER = 'cybersource-routing-number';
const CYBERSOURCE_ACCOUNT_NUMBER_CONTAINER = 'cybersource-account-number';

// Container IDs for NMI Collect.JS hosted fields
const NMI_CARD_NUMBER_CONTAINER = 'nmi-card-number';
const NMI_EXPIRATION_CONTAINER = 'nmi-expiration';
const NMI_CVV_CONTAINER = 'nmi-cvv';
const NMI_ROUTING_NUMBER_CONTAINER = 'nmi-routing-number';
const NMI_ACCOUNT_NUMBER_CONTAINER = 'nmi-account-number';
const NMI_CHECK_NAME_CONTAINER = 'nmi-check-name';

// Container IDs for Adyen Custom Card secured fields. Adyen cards use
// hosted iframes like NMI (3 fields); Adyen ACH uses the plain inputs —
// bank account details are not PCI data and go to the API directly.
const ADYEN_CARD_NUMBER_CONTAINER = 'adyen-card-number';
const ADYEN_EXPIRATION_CONTAINER = 'adyen-expiration';
const ADYEN_CVV_CONTAINER = 'adyen-cvv';

export const PaymentMethodForm: React.FC<PaymentMethodFormProps> = ({
	mode,
	supportedPaymentMethods = [],
	editPaymentMethod,
	onSubmit,
	onCancel,
	showTypeSelector = true,
	allowedPaymentTypes = [
		PaymentMethodTypeEnum.CREDIT_CARD,
		PaymentMethodTypeEnum.ACH,
	],
	preSelectedPaymentType,
	isEditMode = false,
	className,
	submitButtonClassName,
	cancelButtonClassName,
	processorType,
	acceptedCardBrands,
	onSecureFieldsReady,
	onSecureExpirationFieldReady,
	onSecureACHFieldsReady,
	onPaymentTypeChange,
	defaultEmail,
}) => {
	const colors = useThemeColors();
	const { t } = useTranslation();

	const [formData, setFormData] = useState<PaymentMethodFormData>(() =>
		getInitialFormData(preSelectedPaymentType, defaultEmail),
	);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const authNetServiceRef = useRef<AuthorizeNetFrontendService | null>(null);

	// Initialize form data for edit mode
	useEffect(() => {
		if (isEditMode && editPaymentMethod) {
			// Format expiration date for display
			let formattedExpiration = '';

			if (editPaymentMethod.paymentExpirationDate) {
				const expDate = new Date(editPaymentMethod.paymentExpirationDate);
				const month = (expDate.getMonth() + 1).toString().padStart(2, '0');
				const year = expDate.getFullYear().toString();
				formattedExpiration = `${month}/${year}`;
			}

			setFormData({
				paymentType: editPaymentMethod.paymentType,
				// Credit card fields - show masked/display values
				cardNumber: editPaymentMethod.paymentLastFour
					? `****${editPaymentMethod.paymentLastFour}`
					: '',
				expirationDate: formattedExpiration,
				nameOnCard: editPaymentMethod.paymentName || '',
				securityCode: '', // Never show security code
				// eCheck/ACH fields - show display values
				routingNumber: editPaymentMethod.paymentRoutingNumber || '',
				accountNumber: editPaymentMethod.paymentLastFour
					? `****${editPaymentMethod.paymentLastFour}`
					: '',
				nameOnAccount: editPaymentMethod.paymentName || '',
				accountType: editPaymentMethod.accountType || 'checking',
				// Address fields
				addressLine1: editPaymentMethod.paymentAddress1 || '',
				addressLine2: editPaymentMethod.paymentAddress2 || '',
				zipCode: editPaymentMethod.paymentZip || '',
				state: editPaymentMethod.paymentState || '',
				city: editPaymentMethod.paymentCity || '',
				// Email
				email: defaultEmail ?? '',
			});
		}
	}, [editPaymentMethod, defaultEmail, isEditMode]);

	// PD-15014 — the pre-selected payment type is a DEFAULT, not a lock. It
	// used to force formData.paymentType back on every render it differed,
	// which (with the selector disabled) made it impossible to add a card
	// when the parent surface happened to sit on ACH. Apply it only when the
	// PROP changes — the parent moved to a different type — and route the
	// switch through the same path a user-driven change takes so the hosted
	// payment fields are cleaned up and recreated for the new type.
	const prevPreSelectedTypeRef = useRef(preSelectedPaymentType);
	useEffect(() => {
		const prev = prevPreSelectedTypeRef.current;
		prevPreSelectedTypeRef.current = preSelectedPaymentType;
		if (
			isEditMode ||
			preSelectedPaymentType == null ||
			preSelectedPaymentType === prev ||
			formData.paymentType === preSelectedPaymentType
		) {
			return;
		}
		onPaymentTypeChange?.(preSelectedPaymentType);
		setFormData((current) => ({
			...current,
			paymentType: preSelectedPaymentType,
			// Clear fields when switching types, same as a user-driven switch
			cardNumber: '',
			expirationDate: '',
			nameOnCard: '',
			securityCode: '',
			routingNumber: '',
			accountNumber: '',
			nameOnAccount: '',
			accountType: 'checking',
		}));
		setError(null);
	}, [
		preSelectedPaymentType,
		isEditMode,
		formData.paymentType,
		onPaymentTypeChange,
	]);

	// Update email when defaultEmail changes (e.g., when client data loads)
	useEffect(() => {
		if (defaultEmail != null && formData.email === '') {
			setFormData((prev) => ({
				...prev,
				email: defaultEmail,
			}));
		}
	}, [defaultEmail, formData.email]);

	// Cleanup on unmount
	useEffect(
		() => () => {
			if (authNetServiceRef.current) {
				authNetServiceRef.current.cleanup();
			}
		},
		[],
	);

	// Notify parent when CyberSource secure card field containers are ready
	useEffect(() => {
		if (
			processorType === 'cybersource' &&
			formData.paymentType === PaymentMethodTypeEnum.CREDIT_CARD &&
			!isEditMode &&
			onSecureFieldsReady != null
		) {
			const frameId = requestAnimationFrame(() => {
				const cardContainer = document.getElementById(
					CYBERSOURCE_CARD_NUMBER_CONTAINER,
				);
				const cvvContainer = document.getElementById(
					CYBERSOURCE_SECURITY_CODE_CONTAINER,
				);

				if (cardContainer != null && cvvContainer != null) {
					onSecureFieldsReady(
						CYBERSOURCE_CARD_NUMBER_CONTAINER,
						CYBERSOURCE_SECURITY_CODE_CONTAINER,
					);
				}
			});

			return () => {
				cancelAnimationFrame(frameId);
			};
		}

		return undefined;
	}, [processorType, formData.paymentType, isEditMode, onSecureFieldsReady]);

	// Notify parent when NMI secure card field containers are ready
	useEffect(() => {
		if (
			processorType === 'nmi' &&
			formData.paymentType === PaymentMethodTypeEnum.CREDIT_CARD &&
			!isEditMode &&
			onSecureFieldsReady != null
		) {
			const frameId = requestAnimationFrame(() => {
				const cardContainer = document.getElementById(
					NMI_CARD_NUMBER_CONTAINER,
				);
				const cvvContainer = document.getElementById(NMI_CVV_CONTAINER);
				const expContainer = document.getElementById(NMI_EXPIRATION_CONTAINER);

				if (cardContainer != null && cvvContainer != null) {
					onSecureFieldsReady(NMI_CARD_NUMBER_CONTAINER, NMI_CVV_CONTAINER);
				}

				// NMI also needs the expiration field
				if (expContainer != null && onSecureExpirationFieldReady != null) {
					onSecureExpirationFieldReady(NMI_EXPIRATION_CONTAINER);
				}
			});

			return () => {
				cancelAnimationFrame(frameId);
			};
		}

		return undefined;
	}, [
		processorType,
		formData.paymentType,
		isEditMode,
		onSecureFieldsReady,
		onSecureExpirationFieldReady,
	]);

	// Notify parent when Adyen secured card field containers are ready
	useEffect(() => {
		if (
			processorType === 'adyen' &&
			formData.paymentType === PaymentMethodTypeEnum.CREDIT_CARD &&
			!isEditMode &&
			onSecureFieldsReady != null
		) {
			const frameId = requestAnimationFrame((): void => {
				const cardContainer = document.getElementById(
					ADYEN_CARD_NUMBER_CONTAINER,
				);
				const cvvContainer = document.getElementById(ADYEN_CVV_CONTAINER);
				const expContainer = document.getElementById(
					ADYEN_EXPIRATION_CONTAINER,
				);

				if (cardContainer != null && cvvContainer != null) {
					onSecureFieldsReady(ADYEN_CARD_NUMBER_CONTAINER, ADYEN_CVV_CONTAINER);
				}

				// Adyen also mounts a secured expiration field (like NMI)
				if (expContainer != null && onSecureExpirationFieldReady != null) {
					onSecureExpirationFieldReady(ADYEN_EXPIRATION_CONTAINER);
				}
			});

			return () => {
				cancelAnimationFrame(frameId);
			};
		}

		return undefined;
	}, [
		processorType,
		formData.paymentType,
		isEditMode,
		onSecureFieldsReady,
		onSecureExpirationFieldReady,
	]);

	// Notify parent when CyberSource secure ACH field containers are ready
	useEffect(() => {
		if (
			processorType === 'cybersource' &&
			formData.paymentType === PaymentMethodTypeEnum.ACH &&
			!isEditMode &&
			onSecureACHFieldsReady != null
		) {
			const frameId = requestAnimationFrame(() => {
				const routingContainer = document.getElementById(
					CYBERSOURCE_ROUTING_NUMBER_CONTAINER,
				);
				const accountContainer = document.getElementById(
					CYBERSOURCE_ACCOUNT_NUMBER_CONTAINER,
				);

				if (routingContainer != null && accountContainer != null) {
					onSecureACHFieldsReady(
						CYBERSOURCE_ROUTING_NUMBER_CONTAINER,
						CYBERSOURCE_ACCOUNT_NUMBER_CONTAINER,
					);
				}
			});

			return () => {
				cancelAnimationFrame(frameId);
			};
		}

		return undefined;
	}, [processorType, formData.paymentType, isEditMode, onSecureACHFieldsReady]);

	// Notify parent when NMI secure ACH field containers are ready
	useEffect(() => {
		if (
			processorType === 'nmi' &&
			formData.paymentType === PaymentMethodTypeEnum.ACH &&
			!isEditMode &&
			onSecureACHFieldsReady != null
		) {
			const frameId = requestAnimationFrame(() => {
				const routingContainer = document.getElementById(
					NMI_ROUTING_NUMBER_CONTAINER,
				);
				const accountContainer = document.getElementById(
					NMI_ACCOUNT_NUMBER_CONTAINER,
				);
				const nameContainer = document.getElementById(NMI_CHECK_NAME_CONTAINER);

				if (
					routingContainer != null &&
					accountContainer != null &&
					nameContainer != null
				) {
					onSecureACHFieldsReady(
						NMI_ROUTING_NUMBER_CONTAINER,
						NMI_ACCOUNT_NUMBER_CONTAINER,
						NMI_CHECK_NAME_CONTAINER,
					);
				}
			});

			return () => {
				cancelAnimationFrame(frameId);
			};
		}

		return undefined;
	}, [processorType, formData.paymentType, isEditMode, onSecureACHFieldsReady]);

	const handleInputChange = (
		field: keyof PaymentMethodFormData,
		value: string,
	) => {
		setFormData((prev) => {
			const updatedData = {
				...prev,
				[field]: value,
			};

			// If card number is being updated, detect the credit card subtype
			if (field === 'cardNumber' && value.length >= 6) {
				const detectedSubtype = detectCreditCardSubtype(value);
				if (detectedSubtype) {
					// Note: We don't set the subtype in form data since it's not part of the form
					// but we'll pass it to the API when creating the payment method
				}
			}

			return updatedData;
		});
	};

	const handlePaymentTypeChange = (value: string) => {
		const paymentType = value as PaymentMethodTypeEnum;

		// Notify parent about payment type change (for cleanup during type switch)
		onPaymentTypeChange?.(paymentType);

		setFormData((prev) => ({
			...prev,
			paymentType,
			// Clear fields when switching types
			cardNumber: '',
			expirationDate: '',
			nameOnCard: '',
			securityCode: '',
			routingNumber: '',
			accountNumber: '',
			nameOnAccount: '',
			accountType: 'checking',
		}));
		setError(null);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		// The state dropdown carries no native `required` enforcement, and an
		// edit-mode form may still hold a pre-dropdown raw value — resolve to
		// a clean code here and refuse to submit without one, so nothing
		// unnormalized can reach the vault from this form (PD-15304).
		const stateCode = toStateCode(formData.state);
		if (stateCode === '') {
			setError(t('Please select a billing state.'));
			return;
		}

		try {
			setLoading(true);
			const result = await onSubmit({ ...formData, state: stateCode });

			// Reset the form after a successful submission only (create mode).
			// A handler that resolves `false` has already surfaced a failure to
			// the user — keep the entered data so they can correct and retry.
			if (!isEditMode && result) {
				setFormData(getInitialFormData(preSelectedPaymentType, defaultEmail));
			}
		} catch (error) {
			let errorMessage = t(
				'Failed to process payment method. Please try again.',
			);

			if (error instanceof PaymentGatewayError) {
				switch (error.code) {
					case 'VALIDATION_ERROR':
						errorMessage = error.message;
						break;
					case 'E_WC_05':
						errorMessage = t(
							'Invalid credit card number. Please check and try again.',
						);
						break;
					case 'E_WC_06':
						errorMessage = t(
							'Invalid expiration date. Please check and try again.',
						);
						break;
					case 'E_WC_07':
						errorMessage = t(
							'Invalid security code. Please check and try again.',
						);
						break;
					case 'E_WC_15':
						errorMessage = t('Credit card number and card type do not match.');
						break;
					default:
						errorMessage = t(`Payment error: ${error.message}`);
						break;
				}
			} else if (error instanceof Error) {
				errorMessage = error.message;
			}

			setError(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	// Check if using hosted iframe fields for secure card data entry
	const useSecureHostedFields =
		(processorType === 'cybersource' ||
			processorType === 'nmi' ||
			processorType === 'adyen') &&
		!isEditMode;

	// Resolve the hosted card field container IDs for the active processor
	const cardNumberContainerId =
		processorType === 'nmi'
			? NMI_CARD_NUMBER_CONTAINER
			: processorType === 'adyen'
				? ADYEN_CARD_NUMBER_CONTAINER
				: CYBERSOURCE_CARD_NUMBER_CONTAINER;
	const securityCodeContainerId =
		processorType === 'nmi'
			? NMI_CVV_CONTAINER
			: processorType === 'adyen'
				? ADYEN_CVV_CONTAINER
				: CYBERSOURCE_SECURITY_CODE_CONTAINER;

	// Shared container classes for all hosted iframe fields (card + ACH).
	// NMI: No padding — padding is set inside the iframe's input via customCss
	// so the iframe fills the entire container and any click within the border
	// triggers focus (important for mobile tap targets).
	// CyberSource: Keep standard padding — Flex Microform handles its own sizing.
	// Adyen: size the injected iframes ourselves (PD-16214). The SDK creates
	// them with no dimensions and relies on its CDN stylesheet to stretch them
	// to the container; when that stylesheet is not applied (seen in Brave)
	// each frame falls back to the browser default of 150px, gets clipped by
	// this 38px box, and the text lands wherever the box happened to scroll —
	// with the autofill popup anchored far below the visible field.
	const secureFieldClasses = cn(
		'block w-full text-sm h-[38px] overflow-hidden',
		processorType !== 'nmi' && 'px-3 py-2',
		processorType === 'adyen' &&
			'[&>iframe]:block [&>iframe]:h-full [&>iframe]:w-full [&>iframe]:border-0',
		colors.classes.formFieldBase,
		colors.classes.formFieldDefault,
		colors.classes.formFieldFocus,
	);

	// PD-16214 — in edit mode the expiration date is editable only where the
	// processor will take it onto the stored token (see
	// supportsExpirationDateUpdate); elsewhere it is shown for reference, the
	// same way the card number is.
	const expirationIsDisplayOnly =
		isEditMode &&
		processorType != null &&
		!supportsExpirationDateUpdate(processorType);

	const renderCreditCardFields = () => {
		const loadingPlaceholder = (width: string) => (
			<div className="h-full flex items-center">
				<div className={`animate-pulse bg-control h-4 ${width} rounded`} />
			</div>
		);

		return (
			<div className="space-y-4">
				{/* Card Number Field */}
				<div>
					<label
						className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
					>
						{t('Card Number')}
						{!isEditMode && <span className="text-danger ml-1">*</span>}
						{isEditMode && (
							<span className={`ml-2 text-xs ${colors.classes.textSecondary}`}>
								({t('Display only')})
							</span>
						)}
					</label>
					{/* PD-15878 — the marks this merchant account actually takes.
					    Rendered only when the processor could tell us: an empty
					    strip would read as "we accept nothing", which is worse
					    than no strip at all. */}
					{acceptedCardBrands != null && acceptedCardBrands.length > 0 && (
						<div className="flex items-center gap-2 mb-2">
							{acceptedCardBrands.map((brand) => (
								<CardBrandIcon
									key={brand}
									brand={brand}
									className={`text-xl ${colors.classes.textSecondary}`}
								/>
							))}
						</div>
					)}
					{useSecureHostedFields ? (
						<div id={cardNumberContainerId} className={secureFieldClasses}>
							{loadingPlaceholder('w-32')}
						</div>
					) : (
						<Input
							type="text"
							value={formData.cardNumber}
							onChange={(e) => {
								const formatted = formatCardNumber(e.target.value);
								handleInputChange('cardNumber', formatted);
							}}
							placeholder={t('1234 5678 9012 3456')}
							required={!isEditMode}
							disabled={loading || isEditMode}
							maxLength={19}
						/>
					)}
				</div>

				{/* Name on Card Field */}
				<div>
					<label
						className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
					>
						{t('Name on Card')}
						<span className="text-danger ml-1">*</span>
					</label>
					<Input
						type="text"
						value={formData.nameOnCard}
						onChange={(e) => {
							handleInputChange('nameOnCard', e.target.value);
						}}
						placeholder={t('Full name as shown on card')}
						required
						disabled={loading}
					/>
				</div>

				{/* Expiration Date and Security Code Row */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<div>
						<label
							className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
						>
							{t('Expiration Date')}
							{!expirationIsDisplayOnly && (
								<span className="text-danger ml-1">*</span>
							)}
							{expirationIsDisplayOnly && (
								<span
									className={`ml-2 text-xs ${colors.classes.textSecondary}`}
								>
									({t('Display only')})
								</span>
							)}
						</label>
						{/* NMI and Adyen use a hosted field for expiration; CyberSource uses a regular input */}
						{(processorType === 'nmi' || processorType === 'adyen') &&
						!isEditMode ? (
							<div
								id={
									processorType === 'adyen'
										? ADYEN_EXPIRATION_CONTAINER
										: NMI_EXPIRATION_CONTAINER
								}
								className={secureFieldClasses}
							>
								{loadingPlaceholder('w-16')}
							</div>
						) : (
							<Input
								type="text"
								value={formData.expirationDate}
								onChange={(e) => {
									const formatted = formatExpirationDate(e.target.value);
									handleInputChange('expirationDate', formatted);
								}}
								placeholder={t('MM/YY')}
								required={!expirationIsDisplayOnly}
								disabled={loading || expirationIsDisplayOnly}
								maxLength={7}
							/>
						)}
					</div>

					<div>
						<label
							className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
						>
							{t('Security Code')}
							{!isEditMode && <span className="text-danger ml-1">*</span>}
							{isEditMode && (
								<span
									className={`ml-2 text-xs ${colors.classes.textSecondary}`}
								>
									({t('Not required for updates')})
								</span>
							)}
						</label>
						{useSecureHostedFields ? (
							<div id={securityCodeContainerId} className={secureFieldClasses}>
								{loadingPlaceholder('w-12')}
							</div>
						) : (
							<Input
								type="text"
								value={formData.securityCode}
								onChange={(e) => {
									const digits = e.target.value.replace(/\D/g, '');
									handleInputChange('securityCode', digits);
								}}
								placeholder={t('123')}
								required={!isEditMode}
								disabled={loading || isEditMode}
								maxLength={4}
							/>
						)}
					</div>
				</div>
			</div>
		);
	};

	// Check if we should use hosted secure fields for ACH. Adyen ACH uses
	// the plain inputs — bank details are sent to the API as-is (not PCI).
	const useSecureACHFields =
		(processorType === 'cybersource' || processorType === 'nmi') && !isEditMode;

	const renderECheckFields = () => {
		// Determine which container IDs to use based on processor
		const routingContainerId =
			processorType === 'nmi'
				? NMI_ROUTING_NUMBER_CONTAINER
				: CYBERSOURCE_ROUTING_NUMBER_CONTAINER;
		const accountContainerId =
			processorType === 'nmi'
				? NMI_ACCOUNT_NUMBER_CONTAINER
				: CYBERSOURCE_ACCOUNT_NUMBER_CONTAINER;

		return (
			<div className="space-y-4">
				{useSecureACHFields && (
					<div
						className={`p-3 rounded-md ${colors.classes.bgSecondary} border ${colors.classes.borderDefault}`}
					>
						<Text className={`text-sm ${colors.classes.textSecondary}`}>
							{t(
								'To verify your bank account, a temporary $0.01 charge will be made and immediately refunded. This may take a few days to appear on your statement.',
							)}
						</Text>
					</div>
				)}
				<div>
					<label
						className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
					>
						{t('Account Type')}
					</label>
					<Select
						value={formData.accountType}
						onChange={(value) => {
							handleInputChange('accountType', value);
						}}
						options={[
							{ value: 'checking', label: t('Checking') },
							{ value: 'savings', label: t('Savings') },
						]}
						disabled={loading}
					/>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<div>
						<label
							className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
						>
							{t('Routing Number')}
							{!isEditMode && <span className="text-danger ml-1">*</span>}
							{isEditMode && (
								<span
									className={`ml-2 text-xs ${colors.classes.textSecondary}`}
								>
									({t('Display only')})
								</span>
							)}
						</label>
						{useSecureACHFields ? (
							<div id={routingContainerId} className={secureFieldClasses} />
						) : (
							<Input
								type="text"
								value={formData.routingNumber}
								onChange={(e) => {
									handleInputChange('routingNumber', e.target.value);
								}}
								placeholder={t('123456789')}
								required={!isEditMode}
								disabled={loading || isEditMode}
							/>
						)}
					</div>

					<div>
						<label
							className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
						>
							{t('Account Number')}
							{!isEditMode && <span className="text-danger ml-1">*</span>}
							{isEditMode && (
								<span
									className={`ml-2 text-xs ${colors.classes.textSecondary}`}
								>
									({t('Display only')})
								</span>
							)}
						</label>
						{useSecureACHFields ? (
							<div id={accountContainerId} className={secureFieldClasses} />
						) : (
							<Input
								type="text"
								value={formData.accountNumber}
								onChange={(e) => {
									handleInputChange('accountNumber', e.target.value);
								}}
								placeholder={t('Account number')}
								required={!isEditMode}
								disabled={loading || isEditMode}
							/>
						)}
					</div>
				</div>

				<div>
					<label
						className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
					>
						{t('Name on Account')}
						<span className="text-danger ml-1">*</span>
					</label>
					{/* NMI requires name-on-account (checkname) to be a hosted field —
					    Collect.JS never generates an ACH token without it */}
					{useSecureACHFields && processorType === 'nmi' ? (
						<div id={NMI_CHECK_NAME_CONTAINER} className={secureFieldClasses} />
					) : (
						<Input
							type="text"
							value={formData.nameOnAccount}
							onChange={(e) => {
								handleInputChange('nameOnAccount', e.target.value);
							}}
							placeholder={t('Full name as shown on account')}
							required
							disabled={loading}
						/>
					)}
				</div>
			</div>
		);
	};

	const renderBillingAddressFields = () => (
		<div className="space-y-4">
			<div>
				<label
					className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
				>
					{t('Address Line 1')}
					<span className="text-danger ml-1">*</span>
				</label>
				<Input
					type="text"
					value={formData.addressLine1}
					onChange={(e) => {
						handleInputChange('addressLine1', e.target.value);
					}}
					placeholder={t('Street address')}
					required
					disabled={loading}
				/>
			</div>

			<div>
				<label
					className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
				>
					{t('Address Line 2')} {t('(Optional)')}
				</label>
				<Input
					type="text"
					value={formData.addressLine2}
					onChange={(e) => {
						handleInputChange('addressLine2', e.target.value);
					}}
					placeholder={t('Apartment, suite, etc.')}
					disabled={loading}
				/>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
				<div>
					<label
						className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
					>
						{t('City')}
						<span className="text-danger ml-1">*</span>
					</label>
					<Input
						type="text"
						value={formData.city}
						onChange={(e) => {
							handleInputChange('city', e.target.value);
						}}
						placeholder={t('City')}
						required
						disabled={loading}
					/>
				</div>

				<div>
					<label
						className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
					>
						{t('State')}
						<span className="text-danger ml-1">*</span>
					</label>
					<Select
						value={toStateCode(formData.state)}
						onChange={(value) => {
							handleInputChange('state', value);
						}}
						options={US_STATES.map((state) => ({
							value: state.value,
							label: t(state.label),
						}))}
						placeholder={t('Select state')}
						disabled={loading}
					/>
				</div>

				<div>
					<label
						className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
					>
						{t('ZIP Code')}
						<span className="text-danger ml-1">*</span>
					</label>
					<Input
						type="text"
						value={formData.zipCode}
						onChange={(e) => {
							handleInputChange('zipCode', e.target.value);
						}}
						placeholder={t('12345')}
						required
						disabled={loading}
					/>
				</div>
			</div>

			{/* Email field - required for CyberSource */}
			{processorType === 'cybersource' && (
				<div>
					<label
						className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
					>
						{t('Email')}
						<span className="text-danger ml-1">*</span>
					</label>
					<Input
						type="email"
						value={formData.email}
						onChange={(e) => {
							handleInputChange('email', e.target.value);
						}}
						placeholder={t('email@example.com')}
						required
						disabled={loading}
					/>
				</div>
			)}
		</div>
	);

	const paymentTypeOptions = allowedPaymentTypes.map((type) => ({
		value: type,
		label: t(type),
	}));

	return (
		<div className={cn('space-y-6', className)}>
			<form
				id="payment-method-form"
				onSubmit={handleSubmit}
				className="space-y-6"
			>
				{/* Payment Type Selector */}
				{showTypeSelector && paymentTypeOptions.length > 1 && (
					<div>
						<label
							className={`block text-sm font-medium ${colors.classes.textPrimary} mb-1`}
						>
							{t('Payment Type')}
						</label>
						{/* PD-15014 — a pre-selected type must not disable the
						    selector: it is the parent surface's default, and the
						    user still needs to be able to add the OTHER type
						    (e.g. a card while the page sits on ACH). */}
						<Select
							value={formData.paymentType}
							onChange={handlePaymentTypeChange}
							options={paymentTypeOptions}
							disabled={loading}
						/>
					</div>
				)}

				{/* Payment Method Fields */}
				{formData.paymentType === PaymentMethodTypeEnum.CREDIT_CARD &&
					renderCreditCardFields()}
				{formData.paymentType === PaymentMethodTypeEnum.ACH &&
					renderECheckFields()}

				{/* Billing Address */}
				<div>
					<Text
						className={`text-lg font-semibold ${colors.classes.textPrimary} mb-4`}
					>
						{t('Billing Address')}
					</Text>
					{renderBillingAddressFields()}
				</div>

				{/* Error Display */}
				{error && (
					<div className={`p-4 rounded-md ${colors.classes.calloutDanger} `}>
						<Text className={colors.classes.textOnDanger}>{error}</Text>
					</div>
				)}

				{/* Form Actions - only show for non-inline modes */}
				{mode !== 'inline' && (
					<div className="flex justify-end gap-3 pt-4">
						{onCancel && (
							<Button
								type="button"
								variant="outline"
								onClick={onCancel}
								disabled={loading}
								className={cancelButtonClassName}
							>
								{t('Cancel')}
							</Button>
						)}
						<Button
							type="submit"
							variant="primary"
							loading={loading}
							disabled={loading}
							className={submitButtonClassName}
						>
							{loading
								? t('Processing...')
								: mode === 'edit'
									? t('Update Payment Method')
									: t('Add Payment Method')}
						</Button>
					</div>
				)}
			</form>
		</div>
	);
};
